import { useDialog } from "@fpsoftware/mvp";
import { useQueryClient } from "@tanstack/react-query";
import { type ComponentProps, useCallback, useEffect, useState } from "react";
import { type Control, type FieldPath, useController } from "react-hook-form";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { TbMapPin, TbPlus } from "react-icons/tb";
import { getAddressLabel } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { AddAddressDialog } from "@/addresses/AddAddressDialog";
import { useAddressPlacesSearch, useSuspenseAddress } from "@/addresses/hooks";
import { Api } from "@/api.config";
import {
  AutocompleteInput,
  type Option,
} from "@/components/form/AutocompleteInput";
import { QueryKeys } from "@/LinkGen";

interface AddressSelectProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentProps<typeof AutocompleteInput>,
    "onSelect" | "options"
  > {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}
export function AddressSelect<TFieldValues extends FieldValues>(
  props: AddressSelectProps<TFieldValues>,
) {
  const { control, name, ...rest } = props;
  const {
    field: { value, onChange, onBlur },
  } = useController({ control, name });
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const selected = useSuspenseAddress(value as number | null | undefined);
  const [inputValue, setInputValue] = useState("");
  const [debounced] = useDebounceValue(inputValue, 500);
  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());
  const { isLoading: searchLoading, data: suggestions } =
    useAddressPlacesSearch(debounced, sessionToken);

  const options: Option<string>[] = [];
  if (inputValue.length >= 1) {
    options.push({ icon: TbPlus, label: "Add manually", value: "new" });
  }
  for (const suggestion of suggestions ?? []) {
    options.push({
      icon: TbMapPin,
      label: suggestion.label,
      value: `place:${suggestion.placeId}`,
    });
  }

  useEffect(() => {
    if (selected) {
      setInputValue(getAddressLabel(selected));
    }
  }, [selected]);

  const handlePlaceSelect = useCallback(
    async (placeId: string, label: string) => {
      setInputValue(label);
      const address = await Api.AddressPlacesSelect({
        placeId,
        sessionToken,
      });
      queryClient.setQueryData(QueryKeys.Address(address.ID), () => address);
      onChange(address.ID);
      onBlur();
      // Session token must be rotated after a place is finalized.
      setSessionToken(crypto.randomUUID());
    },
    [sessionToken, queryClient, onChange, onBlur],
  );

  const handleChange = (option: Option<string>) => {
    if (option.value === "new") {
      dialog.open(AddAddressDialog, {
        onConfirm: onChange,
      });
      return;
    }

    if (option.value.startsWith("place:")) {
      const placeId = option.value.slice("place:".length);
      void handlePlaceSelect(placeId, option.label);
    }
  };

  return (
    <AutocompleteInput
      loading={inputValue !== "" && (searchLoading || debounced !== inputValue)}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      options={options}
      onSelect={handleChange}
      {...rest}
    />
  );
}
